import { CSSObject } from '@emotion/react';
import { BreakpointKey, HoneycombTheme, getSizeValue } from '../../../theming';
import { StyleProperty, SystemPropKeys, SYSTEM_PROPS, SystemValueType } from './systemProps';

type StyleResponsiveValue = { [key in BreakpointKey]?: string | number };
type StyleValue = string | number | StyleResponsiveValue;

/**
 * Resolves a system prop value into a raw CSS value string.
 * Handles different system value types: spacing, size, color, shadow, identity.
 *
 * @param valueType - The type of the system value
 * @param value - The raw value to resolve
 * @param theme - The Honeycomb theme object
 * @returns The resolved CSS value as a string
 */
function getValue(valueType: SystemValueType, value: string | number, theme: HoneycombTheme): string {
    switch (valueType) {
        case 'color':
            return theme.fn.resolveColorValue(value as string);
        case 'spacing':
            return theme.fn.getSpacingValue(value);
        case 'shadow':
            return theme.fn.resolveShadowValue(value as string);
        case 'size':
            return getSizeValue(value);
        default:
            // 'identity'
            return value.toString();
    }
}

/**
 * Converts a CSS property (or array of properties) and a value into a CSSObject.
 *
 * @param property - CSS property name or array of property names
 * @param value - CSS value to assign
 * @returns A CSSObject mapping property/properties to value
 */
function getCSSProperties(property: StyleProperty, value: string | number): CSSObject {
    return Object.fromEntries((Array.isArray(property) ? property : [property]).map((prop) => [prop, value]));
}

/**
 * Converts a system prop key and value into a CSSObject, handling responsive breakpoints and optional transforms.
 *
 * Supports:
 * - Responsive values (objects keyed by breakpoint)
 * - Logical CSS properties (inline/block for RTL support)
 * - Optional per-prop transforms (e.g., textAlign left → start)
 *
 * @param propKey - The system prop key (e.g., 'pl', 'ta', 'ml')
 * @param value - The value for the prop, can be single value or responsive object
 * @param theme - The Honeycomb theme object
 * @returns CSSObject representing the resolved CSS rules for the prop
 */
export function getResponsiveValue(propKey: SystemPropKeys, value: StyleValue, theme: HoneycombTheme): CSSObject {
    const systemProp = SYSTEM_PROPS[propKey];
    const { property, type: valueType, transform } = systemProp;

    const applyTransform = (val: string | number) => (transform ? transform(val) : val);

    if (typeof value === 'object') {
        // Responsive object
        return theme.fn.breakpoints
            .getSortedKeys(Object.keys(value) as BreakpointKey[])
            .reduce<CSSObject>((acc, breakpoint) => {
                const breakpointValue = value[breakpoint];
                if (breakpointValue === undefined) return acc;

                const resolvedValue = applyTransform(getValue(valueType, breakpointValue, theme));
                const cssProperties = getCSSProperties(property, resolvedValue);

                return breakpoint === 'xs'
                    ? { ...acc, ...cssProperties } // base value, no media query
                    : { ...acc, [theme.fn.breakpoints.up(breakpoint)]: cssProperties };
            }, {});
    }

    // Single value
    const resolvedValue = applyTransform(getValue(valueType, value, theme));
    return getCSSProperties(property, resolvedValue);
}
